import React from "react";
import {
  dexie,
  reopenDexie,
  hasClearDBFlag,
  removeClearDBFlag,
  clearIndexDB,
  DEXIE_STORAGE_DBLOG
} from "dexieDB/dexie";
import { windowReloadLimited } from "tools/windowReload";
import { useEffectOnce } from "react-use";
import { useDBLog } from "hooks/useDBLog";

// when app is paused, close the DB
// when app is resumed, reopen the DB
const DexieInitializer: React.FC = () => {
  const dblog = useDBLog();

  useEffectOnce(() => {
    (async () => {
      // wait to make sure indexDB is ready
      await new Promise((resolve) => setTimeout(resolve, 500));

      // If a previous run requested clearing, do it now
      if (hasClearDBFlag()) {
        removeClearDBFlag();
        try {
          await clearIndexDB();
        } catch (e) {
          dblog(DEXIE_STORAGE_DBLOG, "DexieInitializer failed to clear IndexDB", { error: e });
        }
      }

      // Ensure DB is open on start
      try {
        await reopenDexie();
      } catch (e) {
        dblog(DEXIE_STORAGE_DBLOG, "DexieInitializer failed to reopen Dexie", { error: e });
      }
    })();

    const onPause = () => {
      try {
        if (dexie.isOpen()) dexie.close();
      } catch {}
    };

    const onResume = () => {
      reopenDexie().catch((e) => {
        dblog(DEXIE_STORAGE_DBLOG, "DexieInitializer failed to reopen Dexie on resume", { error: e });
        windowReloadLimited(undefined, { reason: "DexieInitializer" });
      });
    };

    document.addEventListener("pause", onPause, false);
    document.addEventListener("resume", onResume, false);

    return () => {
      document.removeEventListener("pause", onPause, false);
      document.removeEventListener("resume", onResume, false);
    };
  });

  return null;
};

export default DexieInitializer;
